"use client";

import { useMemo, useState } from "react";
import { useCategoriesTree } from "../hook/useCategories";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSub,
    DropdownMenuSubTrigger,
    DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { Menu, ChevronDown } from "lucide-react";
import Link from "next/link";
import { cn } from "@/lib/utils";

import { CategoryTreeNode } from "@/types/category.types";


export const CategoriesDropdown = () => {
    const { data: categories, error } = useCategoriesTree();
    const [open, setOpen] = useState(false);

    const parentCategories = useMemo(() => {
        if (!categories) return [];
        return Array.isArray(categories) ? categories : [] as CategoryTreeNode[];
    }, [categories]);



    if (error || !parentCategories.length) return null;

    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
                <button
                    className={cn(
                        "flex items-center gap-1.5 text-sm font-semibold text-primary-900 focus:outline-none transition-colors group",
                        open ? "text-primary-700 font-bold" : "hover:text-primary-700"
                    )}
                >
                    <Menu className="h-4 w-4" />
                    Catégories
                    <ChevronDown className={cn(
                        "h-3.5 w-3.5 transition-transform opacity-40 group-hover:opacity-100",
                        open && "rotate-180 opacity-100"
                    )} />
                </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
                align="start"
                className="w-64 mt-2 p-1.5 bg-white/95 backdrop-blur-sm border-primary-100 shadow-xl overflow-visible"
                sideOffset={10}
            >
                {parentCategories.map((category: CategoryTreeNode) => {
                    const hasChildren = category.children && category.children.length > 0;

                    if (hasChildren) {
                        return (
                            <DropdownMenuSub key={category.id}>
                                <DropdownMenuSubTrigger
                                    className="py-2.5 px-3 rounded-md hover:bg-primary-50 focus:bg-primary-50 cursor-pointer data-[state=open]:bg-primary-50"
                                >
                                    <Link
                                        href={`/category/${category.slug}/products`}
                                        className="w-full text-primary-800 font-semibold flex items-center gap-2"
                                        onClick={() => setOpen(false)}
                                    >

                                        <span className="line-clamp-1">{category.name}</span>
                                    </Link>
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent
                                    className="w-64 p-1.5 bg-white/95 backdrop-blur-sm border-primary-100 shadow-xl"
                                    sideOffset={12}
                                >
                                    {category.children.map((child: CategoryTreeNode) => (
                                        <DropdownMenuItem key={child.id} asChild className="p-0">
                                            <Link
                                                href={`/category/${child.slug}/products`}
                                                className="w-full py-2.5 px-4 text-sm text-primary-700 hover:text-primary-900 hover:bg-primary-50 cursor-pointer block rounded-md transition-colors"
                                                onClick={() => setOpen(false)}
                                            >
                                                {child.name}
                                            </Link>
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuSubContent>
                            </DropdownMenuSub>
                        );
                    }

                    return (
                        <DropdownMenuItem key={category.id} asChild className="p-0">
                            <Link
                                href={`/category/${category.slug}/products`}
                                className="w-full py-2.5 px-3 text-sm font-semibold text-primary-800 hover:text-primary-900 hover:bg-primary-50 cursor-pointer flex items-center gap-2 rounded-md transition-colors"
                                onClick={() => setOpen(false)}
                            >
                                <span className="line-clamp-1">{category.name}</span>
                            </Link>
                        </DropdownMenuItem>
                    );
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
